import {
	FC,
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import AppleLogin from 'react-apple-login';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Button, Loader } from '@storybook';

import { useNetwork, useNotification, useWebAuthn } from 'hooks';
import { isMacOrIOS, emailRegex, isSkipPhoneEnabled } from 'utils';
import { MESSAGE } from 'constant';
import { isWebAuthnSupported } from 'global-stores';
import {
	ApprovalStatusState,
	CountryCode,
	getWebAuthnSupported,
	LoginCredsState,
	LoginStep,
	MobileLoginLinkResponseState,
	OptionsResponseState,
	useWebLogin,
	VerificationIDState,
	Nosupported,
	ScreenLoader,
	LoginMaskPhoneCredsState,
	userIdbeforeLoginState,
} from 'components';
import { NO_DEVICE_MESSAGE, TEST_USER } from './constant';
import {
	IPasskeySetupState,
	PasskeyBusy,
	PasskeySetup,
} from './passkey-setup';

import style from './choose-method.module.sass';

type ILoginPhoneNumber = {
	phone: string;
	countryCode: string;
};

interface IChooseMethod {
	onSuccess: any;
	onFailed: any;
	locationInfo: Partial<GeolocationCoordinates> | undefined;
	ipAddress: string;
	loginPhoneNumber: ILoginPhoneNumber;
	setSignInActiveStep: any;
	methods: string;
	send_otp_api: string;
}

const params = new URLSearchParams(window.location.search);
const redirectState = params.get('redirectSocialState');

/* Did this page load come from a social-login redirect?
 *
 * `Clients.redirectUrl` (users.api) sends a verified account back to
 * /login?phone=…&countryCode=… after Google or Microsoft has vouched for the
 * email — that pair in the URL is the handoff's signature, and nothing else
 * puts it there (typing credentials by hand leaves the URL bare). Read at
 * module scope like `redirectState` above, which is correct here: the handoff
 * is a full page navigation, so this is evaluated on exactly the load that
 * carries it. */
const isSocialHandoff = !!(params.get('phone') && params.get('countryCode'));

/* ONCE PER PAGE LOAD, not once per mount.
 *
 * A component ref resets when this screen remounts, and Back from the
 * approval chooser remounts it — so a ref-guarded auto-probe fires again,
 * finds no passkey again, and sends the person straight back to the chooser
 * they were trying to leave. Back looks dead because it is immediately undone.
 *
 * Module scope is the right lifetime: the auto-prompt belongs to the arrival
 * from the social redirect (a full page navigation), not to every time this
 * screen happens to mount. After it has run once, Back reaches the list and
 * stays there, which is the whole point of Back. */
let socialAutoPasskeyAttempted = false;

export const ChooseMethod: FC<IChooseMethod> = ({
	onFailed,
	onSuccess,
	locationInfo,
	ipAddress,
	setSignInActiveStep,
	methods,
	send_otp_api,
}) => {
	// globle states
	const setStep = useSetRecoilState(LoginStep);
	const country = useRecoilValue(CountryCode);
	const [verificationID, setVerificationID] = useRecoilState(VerificationIDState);
	const optionsResponse = useRecoilValue(OptionsResponseState);
	const phone = useRecoilValue(LoginCredsState);
	const setSendLinkResponse = useSetRecoilState(MobileLoginLinkResponseState);
	const maskPhoneCreds = useRecoilValue(LoginMaskPhoneCredsState);
	const setuserIdbeforeLogin = useSetRecoilState(userIdbeforeLoginState);

	const verficationStatus = useRecoilValue(ApprovalStatusState);
	const [isNotSupportedOpen, setNotSupportedOpen] =
		useRecoilState(isWebAuthnSupported);

	//lacal state
	const [isBoimetricLoading, setBoimetricLoading] = useState(false);
	const [isMobileAppLoading, setMobileAppLoading] = useState(false);
	const [isWebAuthnCompleted, setWebAuthnCompleted] = useState(false);
	// The passkey card replaces the method list while set. `null` = list.
	const [passkeyState, setPasskeyState] = useState<IPasskeySetupState | null>(
		null
	);
	const [passkeyBusy, setPasskeyBusy] = useState<PasskeyBusy>(null);

	// Every rejection of navigator.credentials.get() lands here — cancel,
	// timeout, "no matching passkey" and the rarer names alike. They are not
	// told apart on purpose: NotAllowedError is what all three common ones
	// throw, and the card's copy is written to be true for each.
	const onPasskeyError = useCallback((error: any, credentialCount = 0) => {
		/* A ceremony that found nothing is the "passkey lives elsewhere" case,
		   not a fault. Chrome and Safari both report it as NotAllowedError —
		   the same name they use when the person simply cancelled — so the two
		   are indistinguishable here, and the card covers both: it offers to
		   set one up on this browser and to try again. Anything else keeps the
		   raw error name, which is what a genuine failure needs. */
		const name = error?.name ?? String(error ?? '');
		if ((name === 'NotAllowedError' || name === 'AbortError') && credentialCount) {
			setPasskeyState({ kind: 'elsewhere', credentialCount });
			return;
		}
		setPasskeyState({ kind: 'failed', phase: 'get', errorName: name });
	}, []);

	// The email or phone typed at sign-in, shaped for `authenticateOpts`.
	const buildAuthPayload = useCallback(() => {
		if (emailRegex.test(phone)) {
			return { type: 'authenticateOpts', email: phone };
		}
		return { type: 'authenticateOpts', countryCode: country, phone };
	}, [country, phone]);

	//hooks
	const {
		registerNewCredential,
		authenticate: authentication,
	} = useWebAuthn();
	const { successNotification, errorNotification } = useNotification();
	const { post: sendOtp } = useNetwork();
	const {
		authenticateOptions,
		verifyRegistration,
		verifyAuthentication,
		registrationOptions,
	} = useWebLogin();

	useEffect(() => {
		if (redirectState) {
			localStorage.setItem('redirectOrigin', redirectState);
		}
	}, []);


	const handleAuthenticationSuccess = useCallback(
		async (res: any) => {
			if (res) {
				setWebAuthnCompleted(true);
				const payload = {
					type: 'verifyAuthentication',
					authenticateOptions: res.authenticateOptions ?? {},
					id: res.id ?? '',
				};
				const resp = await verifyAuthentication(payload);
				const { message = '', isAuthenticated, mfaEnabled, refreshToken, userId, token  } = resp ?? {};
				setuserIdbeforeLogin(userId ?? "")
				if (isAuthenticated) {
					setWebAuthnCompleted(true);
					onSuccess({
						message,
						success: isAuthenticated,
						token,
						type: 'webAuth',
						mfaEnabled : mfaEnabled ?? false,
						refreshToken
					});
				} else {
					// The browser produced an assertion but the server would not
					// accept it (unknown credential, stale challenge, bad
					// signature). Same recovery as a failed ceremony.
					setWebAuthnCompleted(false);
					setPasskeyState({
						kind: 'failed',
						phase: 'get',
						errorName:
							resp?.errorData?.message ??
							message ??
							'server could not verify the passkey',
					});
				}
				return;
			}
			onFailed({
				message: MESSAGE.ERROR,
				success: false,
				type: 'webAuth',
			});
		},
		[onFailed, onSuccess, setuserIdbeforeLogin, verifyAuthentication]
	);

	const handleRegistrastionSuccess = useCallback(
		async (res: any) => {
			if (res) {
				setWebAuthnCompleted(true);
				const payloadSaveCredential = {
					type: 'verifyRegistration',
					registrationOptResponse: res,
					id: optionsResponse?.id,
					verificationId: verificationID,
					deviceInfo: {
						location: locationInfo,
						ip: ipAddress,
					},
				};
				const resp = await verifyRegistration(payloadSaveCredential);
				const { isRegistered, token, mfaEnabled, userId } = resp ?? {};
				if (isRegistered) {
					successNotification(
						mfaEnabled
							? 'Device registered'
							: 'Device registered and logged in successfully.'
					);
					setuserIdbeforeLogin(userId ?? '');
					if (token) {
						setWebAuthnCompleted(true);
						onSuccess({
							message: '',
							success: true,
							token,
							type: 'webAuth',
							mfaEnabled: mfaEnabled ?? false,
						});
						return;
					}
					setWebAuthnCompleted(false);
					// Registered but no token (invited user, phone not yet
					// verified): sign in with the passkey that was just created.
					// It IS in this browser, so `get()` is the right call here.
					const resAuthenticateCredentials = await authenticateOptions(
						buildAuthPayload()
					);
					const { id, errorData } = resAuthenticateCredentials ?? {};
					if (id) {
						authentication(
							handleAuthenticationSuccess,
							resAuthenticateCredentials ?? {},
							onPasskeyError
						);
					} else {
						errorNotification(errorData?.message ?? MESSAGE.ERROR);
					}
					return;
				}
				setWebAuthnCompleted(false);
				onFailed({
					message: MESSAGE.ERROR,
					success: false,
					type: 'webAuth',
				});
				return;
			}
			onFailed({
				message: MESSAGE.ERROR,
				success: false,
				type: 'webAuth',
			});
		},
		[
			onFailed,
			optionsResponse?.id,
			verificationID,
			locationInfo,
			ipAddress,
			verifyRegistration,
			successNotification,
			setuserIdbeforeLogin,
			authenticateOptions,
			buildAuthPayload,
			onSuccess,
			authentication,
			handleAuthenticationSuccess,
			onPasskeyError,
			errorNotification,
		]
	);

	const handleMobileApp = useCallback(async () => {
		if (isMobileAppLoading) return;
		// The approval link is no longer sent from here. The MOBILE_CODE
		// screen asks which channel to use — text or email — and sends only
		// that one (`POST /user-login {type:'maskPhone'|'phone', …, channel}`).
		// An empty verificationId is what tells that screen nothing has gone
		// out yet, so it neither polls nor shows "waiting".
		setMobileAppLoading(true);
		setSendLinkResponse({ verificationId: '' });
		setStep('MOBILE_CODE');
		// setIsWaiting(true);
	}, [isMobileAppLoading, setSendLinkResponse, setStep]);
	/**
	 * Ask the server for the account's passkey list and decide, BEFORE any
	 * browser UI, whether `navigator.credentials.get()` is the right call.
	 *
	 * `force` skips the local-record check — the card's "I do have a passkey
	 * here — try it" — for the one case the record cannot see: a passkey
	 * synced in from another Apple device.
	 *
	 * Returns once the ceremony has resolved or a card state has been chosen,
	 * so callers can hold their spinner for the whole of it.
	 */
	const resolvePasskey = useCallback(
		/* Returns TRUE when the passkey question is settled — the ceremony ran,
		   the account's passkey lives on another device, or a real error was
		   reported. FALSE means the account has no device at all, which is the
		   only state where offering to add one is the right next step.
		   Took a `force` flag until the pre-judgement below was removed; nothing
		   read it after that, and the three call sites all behaved identically,
		   so it went rather than staying as a knob that does nothing. */
		async (): Promise<boolean> => {
			const res = await authenticateOptions(buildAuthPayload());
			const { id, authenticateOptions: opts, errorData } = res ?? {};
			if (!id) {
				const message: string | undefined = errorData?.message;
				if (message && message !== NO_DEVICE_MESSAGE) {
					// "Phone not verified", "User not found", ... — a real error,
					// not the no-passkey state. Say it, keep the list.
					errorNotification(message);
					return true;
				}
				setPasskeyState({ kind: 'none' });
				return false;
			}
			const allow: { id?: string }[] = opts?.allowCredentials ?? [];
			/* TRY THE PASSKEY. DO NOT PRE-JUDGE WHETHER IT IS HERE.
			 *
			 * This used to bail out to the "your passkey is not on this browser"
			 * card whenever `hasLocalCredential` was false — a record of what
			 * THIS browser created. That is the wrong question for a passkey:
			 * the common case on Apple and Google is a SYNCED credential, which
			 * navigator.credentials.get() can use perfectly well even though
			 * this browser never created it. So an account with seven working
			 * passkeys was told none of them were usable and offered to make an
			 * eighth.
			 *
			 * The card's own escape hatch — "I do have a passkey here, try it" —
			 * was the same call with force=true, which is the tell: the browser
			 * already knew the answer, and we were asking the user to overrule a
			 * guess we should not have made.
			 *
			 * The reason for the guess was real — get() with nothing available
			 * raises the native "no matching passkeys" sheet, which the page can
			 * neither style nor dismiss — but that is the FALLBACK case, not the
			 * common one, and it is recoverable: onPasskeyError shows the card
			 * when the ceremony finds nothing. Attempt first, explain second.
			 *
			 * Only an account with NO credentials at all still short-circuits;
			 * there is genuinely nothing to try. */
			if (!allow.length) {
				setPasskeyState({ kind: 'elsewhere', credentialCount: 0 });
				return true;
			}
			await authentication(handleAuthenticationSuccess, res, (err: any) =>
				onPasskeyError(err, allow.length)
			);
			return true;
		},
		[
			authenticateOptions,
			buildAuthPayload,
			errorNotification,
			authentication,
			handleAuthenticationSuccess,
			onPasskeyError,
		]
	);

	/* REGISTRATION IS THE FALLBACK, NOT THE OPENING MOVE — but it is reached
	 * only from the passkey card, and every way onto that card follows an
	 * attempt that already answered the existing-passkey question: a get()
	 * ceremony that found nothing or was rejected, an account with no usable
	 * credential, or a failed create().
	 *
	 * This used to call resolvePasskey() again before registering ("if the
	 * account already has a passkey, use it"). From the card that question is
	 * already answered, and the answer never changes: for an account whose
	 * passkeys live on another device or another domain (every account carried
	 * over from app.simplici.io), it re-ran navigator.credentials.get(), raised
	 * Chrome's native "No passkeys available" sheet, and onPasskeyError put the
	 * same card back. "Set up a passkey on this browser" therefore could never
	 * set one up — a loop with no exit except the approval route.
	 *
	 * "Use the existing passkey first" still happens where it belongs: the
	 * Device Biometric row (handleWebAuth) and the SSO hand-off both start with
	 * resolvePasskey(), and the card's own "I do have a passkey here — try it"
	 * is there for anyone who does.
	 */
	const startRegisterDevice = useCallback(async () => {
		if (verficationStatus === 'approved') {
			registrationOptions();
		} else {
			// The approval link is no longer sent from here. The
			// BIOMETRIC_APPROVAL screen asks which channel to use — text or
			// email — and sends only that one (`registrationApproval` with
			// `channel`). An empty verificationId is what tells that screen
			// nothing has gone out yet.
			setVerificationID('');
			setStep('BIOMETRIC_APPROVAL');
		}
	}, [registrationOptions, setStep, setVerificationID, verficationStatus]);

	// "Set up a passkey" on the card. On success `startRegisterDevice` moves
	// the step to BIOMETRIC_APPROVAL and this component unmounts; on failure
	// it has already toasted, so only the busy flag needs clearing.
	const handleSetUpPasskey = useCallback(async () => {
		if (passkeyBusy) return;
		setPasskeyBusy('setup');
		try {
			await startRegisterDevice();
		} finally {
			setPasskeyBusy(null);
		}
	}, [passkeyBusy, startRegisterDevice]);

	const handleWebAuth = useCallback(async () => {
		if (TEST_USER.includes(phone)) {
			handleMobileApp();
			return;
		}
		if (!(await getWebAuthnSupported())) {
			setNotSupportedOpen(true);
			return;
		}
		if (isBoimetricLoading) return;
		setBoimetricLoading(true);
		try {
			await resolvePasskey();
		} finally {
			setBoimetricLoading(false);
		}
	}, [
		handleMobileApp,
		isBoimetricLoading,
		phone,
		resolvePasskey,
		setNotSupportedOpen,
	]);

	// Card: "I do have a passkey here — try it" / "Try the passkey again".
	const handleTryPasskey = useCallback(async () => {
		if (passkeyBusy) return;
		setPasskeyBusy('passkey');
		try {
			await resolvePasskey();
		} finally {
			setPasskeyBusy(null);
		}
	}, [passkeyBusy, resolvePasskey]);

	/* ARRIVING FROM SOCIAL LOGIN: PROMPT THE PASSKEY, DON'T ASK FIRST.
	 *
	 * Someone who clicked "Sign in with Google" has already said how they want
	 * to sign in. Landing them on a list that asks the same question again —
	 * with the answer they want one row down — is a screen nobody needed, and
	 * it is what made this flow feel broken: SSO appeared to do nothing
	 * because the thing it handed off to looked like a fresh question.
	 *
	 * resolvePasskey() is the whole decision. It asks the server for the
	 * account's credentials and returns TRUE once the question is settled —
	 * the ceremony ran, or the passkey lives on another device, or a real
	 * error was reported and shown. It returns FALSE for exactly one state:
	 * the account has no passkey at all. That is the only case where a chooser
	 * is the right next screen, so that is the only case that falls through to
	 * one.
	 *
	 * This also answers the complaint that removed this screen on 2026-09-15
	 * ("asked the same question twice"): an account WITH a passkey now never
	 * sees the list, and an account WITHOUT one goes straight to the approval
	 * chooser. The duplicate question is gone in both directions, without
	 * taking the passkey path with it.
	 *
	 * Gated on the handoff's own URL shape (`phone` + `countryCode`, what
	 * Clients.redirectUrl builds) so that typing credentials by hand still
	 * reaches the list and its choice. Guarded at MODULE scope, not by a ref:
	 * the ceremony must not start twice (the browser rejects the second call
	 * while the first prompt is open), and a ref would reset on the remount
	 * that Back causes — re-firing the probe and bouncing the person straight
	 * back to the chooser they just left. */
	useEffect(() => {
		if (!isSocialHandoff || socialAutoPasskeyAttempted) return;
		socialAutoPasskeyAttempted = true;
		(async () => {
			const settled = await resolvePasskey();
			if (settled) return;
			// No passkey on this account — the approval chooser is the next
			// screen, and an empty verificationId tells it nothing has been
			// sent yet.
			setSendLinkResponse({ verificationId: '' });
			setStep('MOBILE_CODE');
		})();
		// eslint-disable-next-line
	}, [resolvePasskey]);

	// Card: "Use approval via email / mobile instead" — the same request the
	// list row makes; the card only holds its own busy flag while it runs.
	const handleUseApproval = useCallback(async () => {
		if (passkeyBusy) return;
		setPasskeyBusy('approval');
		try {
			await handleMobileApp();
		} finally {
			setPasskeyBusy(null);
		}
	}, [passkeyBusy, handleMobileApp]);

	const handleBackToMethods = useCallback(() => {
		setPasskeyState(null);
	}, []);

	const handleOtp = useCallback(() => {
		setSignInActiveStep('otpVerify');
		sendOtp(send_otp_api, {
			phone,
			country,
		});
	}, [setSignInActiveStep, sendOtp, send_otp_api, phone, country]);

	const handleGoBack = useCallback(() => {
		setStep('CREDS');
	}, [setStep]);

	const handleEdit = useCallback(() => {
		setStep('CREDS');
	}, [setStep]);

	const handleAppleLogin = useCallback(() => ({}), []);

	const renderAppleLogin = useMemo(
		() =>
			isMacOrIOS && (
				<AppleLogin
					clientId="com.satschel.stage.secure"
					redirectURI={window.location.href}
					usePopup={true}
					callback={handleAppleLogin} // Catch the response
					scope="email name"
					responseMode="query"
					render={() => (
						//Custom Apple Sign in Button
						<div className={style.list_item} onClick={handleWebAuth}>
							<div className={style.left_icon}>
								<i className="ri-apple-line"></i>
							</div>
							<div className={style.method_title}>SignIn With Apple</div>
							<div className={style.right_icon}>
								<i className="ri-arrow-right-s-line"></i>
							</div>
						</div>
					)}
				/>
			),

		[handleAppleLogin, handleWebAuth]
	);

	const chooseMethodArr = useMemo(
		() => [
			{
				category: 'biometric',
				componet: (
					<div
						className={`${style.list_item} ${
							isBoimetricLoading ? style.list_item_disabled : ''
						}`}
						onClick={handleWebAuth}
					>
						<div className={style.left_icon}>
							<i className="ri-fingerprint-line"></i>
						</div>
						<div className={style.method_title}>Device Biometric</div>
						<div className={style.right_icon}>
							{isBoimetricLoading ? (
								<Loader type={'loader'} dimension={24} />
							) : (
								<i className="ri-arrow-right-s-line"></i>
							)}
						</div>
					</div>
				),
			},
			{
				category: 'apple-login',
				componet: renderAppleLogin,
			},
			{
				category: 'approval',
				componet: (
					<div
						className={`${style.list_item} ${
							isMobileAppLoading ? style.list_item_disabled : ''
						}`}
						onClick={isMobileAppLoading ? undefined : handleMobileApp}
					>
						<div className={style.left_icon}>
							<i className="ri-smartphone-line"></i>
						</div>
						<div className={style.method_title}> { `Approval via ${isSkipPhoneEnabled() ? 'email / mobile' : 'mobile'} `}</div>
						<div className={style.right_icon}>
							{isMobileAppLoading ? (
								<Loader type={'loader'} dimension={24} />
							) : (
								<i className="ri-arrow-right-s-line"></i>
							)}
						</div>
					</div>
				),
			},
			{
				category: 'otp',
				componet: (
					<div className={style.list_item} onClick={handleOtp}>
						<div className={style.left_icon}>
							<i className="ri-message-line"></i>
						</div>
						<div className={style.method_title}>
							Mobile Text: One-time password
						</div>
						<div className={style.right_icon}>
							<i className="ri-arrow-right-s-line"></i>
						</div>
					</div>
				),
			},
		],
		[
			handleMobileApp,
			handleOtp,
			handleWebAuth,
			isBoimetricLoading,
			isMobileAppLoading,
			renderAppleLogin,
		]
	);

	const approvalLabel = isSkipPhoneEnabled() ? 'email / mobile' : 'mobile';

	const renderPasskeySetup = useMemo(
		() =>
			passkeyState && (
				<PasskeySetup
					state={passkeyState}
					identity={phone}
					approvalLabel={approvalLabel}
					busy={passkeyBusy}
					onSetUp={handleSetUpPasskey}
					onUseApproval={handleUseApproval}
					onTryPasskey={handleTryPasskey}
					onBack={handleBackToMethods}
				/>
			),
		[
			passkeyState,
			phone,
			approvalLabel,
			passkeyBusy,
			handleSetUpPasskey,
			handleUseApproval,
			handleTryPasskey,
			handleBackToMethods,
		]
	);

	// The passkey card is ADDITIVE: it renders directly beneath the Device
	// Biometric row that produced it, and every row the screen offered
	// before — and the Back button — stays exactly where it was. Nothing on
	// this screen is hidden or gated by the card.
	const renderSelectMethod = useMemo(() => {
		const arr = chooseMethodArr.filter(item =>
			methods?.split('|').includes(item.category)
		);
		return arr.map(item => (
			<Fragment key={item.category}>
				{item.componet}
				{item.category === 'biometric' && renderPasskeySetup}
			</Fragment>
		));
	}, [methods, chooseMethodArr, renderPasskeySetup]);

	// A closed or failed `navigator.credentials.create()` used to reject an
	// unhandled promise and leave the screen untouched. It now lands on the
	// card's `failed` / `create` state, whose actions are the way forward.
	const onCreateError = useCallback((error: any) => {
		setPasskeyState({
			kind: 'failed',
			phase: 'create',
			errorName: error?.name ?? String(error ?? ''),
		});
	}, []);

	// Exactly ONE navigator.credentials.create() per registration-options
	// response. The two conditions this effect used to hold ("first response"
	// and "status is approved") are both true on the first approval, so it
	// called create() twice; the browser rejected the second with
	// NotAllowedError, which was swallowed — and would now read as a failure
	// while the first prompt is still open. A ref on the response object is
	// the dedupe: a retry fetches a NEW object and runs again.
	const handledOptionsRef = useRef<any>(null);
	useEffect(() => {
		if (!optionsResponse || handledOptionsRef.current === optionsResponse) {
			return;
		}
		handledOptionsRef.current = optionsResponse;
		if (optionsResponse.registrationOptions) {
			registerNewCredential(
				handleRegistrastionSuccess,
				optionsResponse.registrationOptions
			).catch(onCreateError);
			return;
		}
		if (optionsResponse.error) {
			// The options fetch after the approval link failed (server-side).
			// Same card, so the person has the approval route and a retry.
			setPasskeyState({
				kind: 'failed',
				phase: 'create',
				errorName: String(optionsResponse.error),
			});
		}
		// eslint-disable-next-line
	}, [optionsResponse]);

	const renderItem = useMemo(
		() => (
			<div className={style.wrapper}>
				<div className={style.inner_wrapper}>
					<div className={style.inner}>
						<div>
							<div className={style.title}>Choose Method</div>
							<div className={style.sub_title}>
								Select the method through which you want sign in.
							</div>
						</div>
						<div className={style.edit}>
							We will send you a confirmation code :{' '}
							<div>
								{country} {maskPhoneCreds}{' '}
								<span className={style.edit_Btn} onClick={handleEdit}>
									Edit
								</span>
							</div>
						</div>
						<div className={style.lists}>{renderSelectMethod}</div>
						<Button
							type="button__filled--secondary button__large button__block"
							label={'Back'}
							handleClick={handleGoBack}
						/>
					</div>
				</div>
			</div>
		),
		[country, handleEdit, handleGoBack, maskPhoneCreds, renderSelectMethod]
	);

	const handalNotSupported = () => {
		setNotSupportedOpen(false);
	};
	return (
		<>
			{renderItem}
			<div className="popup_wrapper">
				<Nosupported
					title={'Look like your browser doesn’t support WebAuthn'}
					visible={isNotSupportedOpen}
					description={'Your browser is does not support WebAuthn.'}
					handleModal={handalNotSupported}
					boldDescription=""
					label={'Close'}
					type="Secondary"
				/>
				{/* Pradeep chaurasia : Screen loader added between webauthn completion and redirect dashboard */}
				<ScreenLoader visible={isWebAuthnCompleted} />
			</div>
		</>
	);
};